import { useState } from "react";
import { Tag, X, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface CouponInputProps {
  onApply: (code: string) => Promise<boolean>;
  appliedCoupon?: string | null;
  discount?: string;
  onRemove?: () => void;
}

export const CouponInput = ({
  onApply,
  appliedCoupon,
  discount,
  onRemove,
}: CouponInputProps) => {
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApply = async () => {
    if (!code.trim()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const success = await onApply(code.trim().toUpperCase());
      if (!success) {
        setError("Neplatný kupón");
      } else {
        setCode("");
      }
    } catch {
      setError("Niečo sa pokazilo");
    } finally {
      setIsLoading(false);
    }
  };

  if (appliedCoupon) {
    return (
      <div className="p-4 bg-accent/50 rounded-xl border border-primary/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <Tag className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">
                Získali ste zľavu {discount} na váš nákup!
              </p>
              <p className="text-sm font-bold text-primary uppercase tracking-wide">
                {appliedCoupon}
              </p>
            </div>
          </div>
          <button
            onClick={onRemove}
            className="p-2 rounded-lg hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
            title="Odstrániť kupón"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="coupon-input">
        <Tag className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <input
          type="text"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            setError(null);
          }}
          onKeyDown={(e) => e.key === "Enter" && handleApply()}
          placeholder="Zadajte zľavový kód"
          className="flex-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground"
        />
        <Button
          onClick={handleApply}
          disabled={!code.trim() || isLoading}
          size="sm"
          className="px-4 rounded-xl"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            "Použiť"
          )}
        </Button>
      </div>
      {error && (
        <p className="text-xs text-destructive px-1">{error}</p>
      )}
    </div>
  );
};
